import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent } from 'components/ui/card';
import { ScrollArea } from 'components/ui/scroll-area';
import { 
  Heart, Send, ArrowLeft, Sparkles, RefreshCw, 
  AlertTriangle, Lightbulb, Coffee, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const QUICK_PROMPTS = [
  { icon: Lightbulb, text: "What should I focus on today?", color: "bg-amber-100 text-amber-600" },
  { icon: AlertTriangle, text: "My loved one is refusing to eat", color: "bg-rose-100 text-rose-600" },
  { icon: Coffee, text: "How do I reduce caregiver burnout?", color: "bg-teal-100 text-teal-600" },
  { icon: Phone, text: "What do I do right now?", color: "bg-blue-100 text-blue-600" },
];

export default function CareGuide() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/ai/history');
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMessage = {
      message_id: `temp_${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/ai/chat', {
        message: text
      });

      const assistantMessage = {
        message_id: `resp_${Date.now()}`,
        role: 'assistant',
        content: response.data.response,
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Daily AI limit reached. Upgrade to Premium for unlimited access.');
        setMessages(prev => prev.slice(0, -1)); // Remove the user message
      } else {
        toast.error('Failed to get response');
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearHistory = async () => {
    try {
      await api.delete('/ai/history');
      setMessages([]);
      toast.success('Chat history cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="care-guide">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-slate-900 font-['Manrope']">AI Care Guide™</h1>
                <p className="text-xs text-slate-500">Powered by advanced AI</p>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={clearHistory}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Clear
          </Button>
        </div>
      </header>

      {/* Chat Area */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-6">
        {messages.length === 0 && !historyLoading ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center mb-6">
              <Sparkles className="w-10 h-10 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Manrope']">
              Hello, {user?.name?.split(' ')[0]}!
            </h2>
            <p className="text-slate-500 mb-8 max-w-md">
              I'm your AI Care Guide. Ask me anything about caring for your loved one—from daily routines to managing stress.
            </p>
            
            <div className="grid grid-cols-2 gap-3 w-full max-w-lg">
              {QUICK_PROMPTS.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => sendMessage(prompt.text)}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all text-left"
                  data-testid={`quick-prompt-${index}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${prompt.color} flex items-center justify-center flex-shrink-0`}>
                    <prompt.icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm text-slate-700">{prompt.text}</span>
                </button>
              ))}
            </div>

            <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200 max-w-md">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Important Disclaimer</p>
                  <p className="mt-1">I'm not a medical professional. For emergencies, always call 911. For medical advice, consult a healthcare provider.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-260px)]">
            <div className="space-y-4 pb-4 pr-4">
              {messages.map((message) => (
                <div
                  key={message.message_id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] ${
                    message.role === 'user' 
                      ? 'chat-bubble-user px-4 py-3' 
                      : 'chat-bubble-assistant px-4 py-3'
                  }`}>
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center">
                          <Sparkles className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-xs font-medium text-slate-500">AI Care Guide</span>
                      </div>
                    )}
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {message.content}
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="chat-bubble-assistant px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center">
                        <Sparkles className="w-3 h-3 text-white animate-pulse" />
                      </div>
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-slate-200 p-4">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about caregiving..."
              className="flex-1 h-12 rounded-full px-6"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              size="icon"
              className="h-12 w-12 rounded-full bg-teal-500 hover:bg-teal-600 text-white"
              disabled={loading || !input.trim()}
              data-testid="send-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          {user?.subscription_tier !== 'premium' && (
            <p className="text-center text-xs text-slate-500 mt-2">
              {5 - (messages.filter(m => m.role === 'user').length % 5)} free messages remaining today.{' '}
              <Link to="/subscription" className="text-teal-600 hover:underline">Upgrade for unlimited</Link>
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
